'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { hasObjectKey } from '@core/utils';
import type { SaveWelloCommentResponse } from '@generated/auth';
import type { Query, QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { delay } from 'es-toolkit';
import type { COMMENT_TYPE } from '@/api';
import { AuthApi, COMMENT_SORT_TYPE } from '@/api';
import { buildOptimisticComment, insertCommentIntoListData, toContentIdQuery } from '@/api/getCommentList';
import { useAuthStore } from '@/stores/AuthStore';
import { COMMENT_PROBE_DELAY_MS, invalidateExceptCommentLists, isWelloCommentListQuery, MAX_COMMENT_PROBE_RETRIES } from './commentListSync';
interface UsePostCommentMutationParams {
  /** 댓글 API에서 댓글 대상 카테고리로 사용 */
  commentType: COMMENT_TYPE;
  /** 댓글이 달리는 컨텐츠 id */
  contentId: number | string;
  /** 답글일 때 부모 댓글 id */
  parentCommentId?: number;
  /** 답글 트리의 최상위 댓글 id */
  rootCommentId?: number;
  /** 답글일 때 멘션 대상(부모 작성자) 닉네임. 낙관적 댓글의 parentNickName에 쓴다 */
  replyTo?: string;
  /** 성공 시 상위 composer에 응답을 전달 */
  onSuccess?: (response?: SaveWelloCommentResponse) => void;
}
const COMMENT_PROBE_PAGE_SIZE = 20;
const matchesContent = (queryParams: object, contentId: number | string) => {
  const contentIdQuery = toContentIdQuery(contentId);
  if ('contentUuid' in contentIdQuery) {
    return hasObjectKey(queryParams, 'contentUuid') && queryParams.contentUuid === contentIdQuery.contentUuid;
  }
  return hasObjectKey(queryParams, 'contentId') && queryParams.contentId === contentIdQuery.contentId;
};

// 해당 컨텐츠의 댓글 목록 쿼리면 쿼리키의 요청 파라미터를 돌려준다.
const getContentCommentListQueryParams = (query: Query<unknown, Error, unknown, QueryKey>, contentId: number | string) => {
  if (!isWelloCommentListQuery(query)) {
    return undefined;
  }
  const [firstQueryKey] = query.queryKey;
  if (!firstQueryKey || typeof firstQueryKey !== 'object' || !hasObjectKey(firstQueryKey, 'query')) {
    return undefined;
  }
  const {
    query: queryParams
  } = firstQueryKey;
  if (!queryParams || typeof queryParams !== 'object') {
    return undefined;
  }
  return matchesContent(queryParams, contentId) ? queryParams : undefined;
};

// 루트 댓글 낙관적 삽입 대상: 1페이지 목록(무한 스크롤 쿼리키도 page=1 고정). 루트 스레드 상세(parentCommentId 지정) 목록은 제외.
const isRootInsertTarget = (queryParams: object) => {
  if (hasObjectKey(queryParams, 'parentCommentId') && queryParams.parentCommentId !== undefined && queryParams.parentCommentId !== null) {
    return false;
  }
  return hasObjectKey(queryParams, 'page') && queryParams.page === 1;
};
interface WaitForCommentListedParams {
  commentType: COMMENT_TYPE;
  contentId: number | string;
  rootCommentId?: number;
  commentId: number;
}

/**
 * 새 댓글이 목록 조회에 반영됐는지 캐시를 거치지 않고 확인한다.
 * refetch로 확인하면 지연 창의 stale 응답이 캐시를 덮어 낙관적 댓글이 사라진다(깜빡임).
 */
const waitForCommentListed = async ({
  commentType,
  contentId,
  rootCommentId,
  commentId
}: WaitForCommentListedParams) => {
  for (let attempt = 0; attempt < MAX_COMMENT_PROBE_RETRIES; attempt++) {
    await delay(COMMENT_PROBE_DELAY_MS);
    try {
      const {
        data
      } = await AuthApi['get:/wello/v2/auth/wello-comment']({
        query: {
          page: 1,
          size: COMMENT_PROBE_PAGE_SIZE,
          commentTypeCd: commentType,
          commentSort: COMMENT_SORT_TYPE.LATEST,
          parentCommentId: rootCommentId,
          ...toContentIdQuery(contentId)
        }
      });
      if (data?.context?.contents?.some(comment => comment.welloCommentId === commentId)) {
        return true;
      }
    } catch {
      // 확인 조회가 실패하면 낙관적 상태를 유지한 채 종료한다.
      return false;
    }
  }
  return false;
};

// 신규 댓글/답글 작성과 관련 쿼리 무효화를 함께 처리한다.
export const usePostCommentMutation = t0 => {
  const $ = _c(23);
  const {
    commentType,
    contentId,
    parentCommentId,
    rootCommentId,
    replyTo,
    onSuccess
  } = t0;
  const {
    toast,
    toastError
  } = useToast();
  const queryClient = useQueryClient();
  const myInfo = useAuthStore(_temp);
  let t1;
  if ($[0] !== commentType || $[1] !== contentId || $[2] !== parentCommentId || $[3] !== rootCommentId) {
    t1 = contents => AuthApi["post:/wello/v2/auth/wello-comment"]({
      body: {
        commentTypeCd: commentType,
        contents,
        parentWelloCommentId: parentCommentId,
        rootWelloCommentId: rootCommentId,
        ...toContentIdQuery(contentId)
      }
    });
    $[0] = commentType;
    $[1] = contentId;
    $[2] = parentCommentId;
    $[3] = rootCommentId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== commentType || $[6] !== contentId || $[7] !== myInfo?.id || $[8] !== myInfo?.nickName || $[9] !== myInfo?.profileImage || $[10] !== onSuccess || $[11] !== parentCommentId || $[12] !== queryClient || $[13] !== replyTo || $[14] !== rootCommentId || $[15] !== toast) {
    t2 = (t3, contents_0) => {
      const {
        data
      } = t3;
      toast({
        type: "success",
        message: "\uB313\uAE00 \uB4F1\uB85D\uC644\uB8CC"
      });
      const newCommentId = data?.context?.welloCommentId;
      const authorId = myInfo?.id;
      const isReply = !!rootCommentId;
      if (newCommentId && authorId) {
        const optimisticComment = buildOptimisticComment({
          welloCommentId: newCommentId,
          contents: contents_0,
          commentType,
          contentId,
          parentCommentId,
          rootCommentId,
          replyTo,
          author: {
            id: authorId,
            nickName: myInfo?.nickName,
            profileImage: myInfo?.profileImage
          }
        });
        queryClient.setQueriesData({
          predicate: query => {
            const queryParams = getContentCommentListQueryParams(query, contentId);
            if (!queryParams) {
              return false;
            }
            return isReply || isRootInsertTarget(queryParams);
          }
        }, previousData => insertCommentIntoListData(previousData, optimisticComment));
      }
      onSuccess?.(data?.context);
      invalidateExceptCommentLists(queryClient);
      if (!newCommentId) {
        return;
      }
      (async () => {
        const isListed = await waitForCommentListed({
          commentType,
          contentId,
          rootCommentId,
          commentId: newCommentId
        });
        if (!isListed) {
          return;
        }
        await queryClient.refetchQueries({
          predicate: query_0 => !!getContentCommentListQueryParams(query_0, contentId),
          type: "active"
        });
      })();
    };
    $[5] = commentType;
    $[6] = contentId;
    $[7] = myInfo?.id;
    $[8] = myInfo?.nickName;
    $[9] = myInfo?.profileImage;
    $[10] = onSuccess;
    $[11] = parentCommentId;
    $[12] = queryClient;
    $[13] = replyTo;
    $[14] = rootCommentId;
    $[15] = toast;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== toastError) {
    t3 = error => {
      toastError({
        error
      });
    };
    $[17] = toastError;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return useMutation(t4);
};
function _temp(state) {
  return state.myInfo;
}